import {  useEffect, useState } from "react";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { getUserAttributes } from "../../utils/auth";
import { getColumns, initialSort } from "./ClientRatingOverviewTableConfig";
import ClientService from "../../services/clients/ClientService";
import { capitalizeFirstLetter, convertToUTC, getNormalizedTimeZone } from "../../utils/utils";
import { Box } from "@mui/system";
import BasicDatatable from "../Common/BasicDatatable";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import UserTeam from "../Analytics/UserTeam";
import { UserInfo, UserTeamData } from "../../utils/types";
import "../../styles/clientPage.css";
import SlideOver from "../Common/SlideOver";
import ClientInteractionActionView from "./ClientInteractionActionView";
import useGlobalContext from "../../context/useGlobalContext";

const generateRows = (
  accountInsights: any,
  isTeamMeeting: boolean,
  selectedNps: string | null,
  loggedInEmail: string | null,
  startEndMeetingTime: any,
  isClientInteraction: boolean,
  onClientClick: (clientDomain: string, clientName: string, slideOver: boolean, clientOrgId: string, subOrgId: string) => void,
  highlightedClient: string | null,
  isSlideOverOpen?: boolean,
  parentPageIdentifier?: string,
  slideOverParent?: string,
  customDateRange?: { startDate: string; endDate?: string },
  clickeduserEmail?: string
) =>
  accountInsights?.map((item: any) => {
    return {
      //todo: confirm
      id: item.clientDomain, // Use domain as unique ID
      clientName: item.clientName, // Use domain as name (API does not include client name)
      domain: item.clientDomain, // Client domain
      num_meetings: item.totalInteractions, // Total interactions
      duration: item.interactionDuration, // Duration of interactions
      last_interaction_time:
        item?.lastInteraction?.length > 0
          ? item.lastInteraction[0].start_time
          : "No Interaction", // Last interaction start time or fallback
      last_interaction_summary:
        item?.lastInteraction?.length > 0
          ? item.lastInteraction[0].summary
          : "", // Last interaction summary
      last_interaction_meeting_id:
        item?.lastInteraction?.length > 0
          ? item.lastInteraction[0].interaction_id
          : "", // Last interaction ID
      last_sentiment: item?.lastThreeRatedInteractions?.length > 0 ? item.lastThreeRatedInteractions : [],
      daysSinceLastInteraction: item?.daysSinceLastInteraction,
      lastInteraction: item?.lastInteractionDate,
      mrr: item.mrr,
      isTeamMeeting: isTeamMeeting,
      selectedNps: selectedNps,
      loggedInEmail: loggedInEmail,
      startEndMeetingTime: startEndMeetingTime,
      isClientInteraction: isClientInteraction,
      onClientClick: onClientClick,
      highlightedClient: highlightedClient,
      parentPageIdentifier: parentPageIdentifier,
      slideOverParent: slideOverParent,
      customDateRange: customDateRange,
      isSlideOverOpen: isSlideOverOpen,
      clickeduserEmail: clickeduserEmail,
      clientOrgId: item?.clientOrgId,
      subOrgId: item?.subOrgId,
    };
  });

type ClientRatingOverviewProps = {
  isSlideOverOpen?: boolean;
  selectedNps?: string | null;
  viewMode?: "count" | "revenue";
  slideOverParent?: string;
  parentPageIdentifier?: string;
  customDateRange?: { startDate: string; endDate: string };
  setCustomDateRange?: (range: { startDate: string; endDate: string }) => void;
  selectedTab?: any;
  clickeduserEmail?: string;
  userEmail?: string;
};

const ClientRatingOverview = ({
  isSlideOverOpen,
  selectedNps,
  viewMode,
  slideOverParent,
  parentPageIdentifier,
  customDateRange,
  setCustomDateRange,
  selectedTab,
  clickeduserEmail,
  userEmail,
}: ClientRatingOverviewProps) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const onPageChange = (value: number) => {
    setCurrentPageIndex(value);
  };
  const handlePageIndexChange = (newPageIndex: number) => {
    onPageChange(newPageIndex + 1);
  };
  const [sort, setSort] = useState(initialSort);
  const { showSnackbar } = useDisplaySnackbar();
  const loggedInEmail = getUserAttributes()?.email;
  const { accountDetails } = useGlobalContext();
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const [accountInsights, setAccountInsights] = useState<any[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [rows, setRows] = useState<any[]>([]);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(50);
  const isClientInteraction = true;
  const [_newUserTeamData, setNewUserTeamData] = useState<UserTeamData | null>(
    null
  );
  const [highlightedClient, setHighlightedClient] = useState<string | null>(
    null
  );
  const [_department, setDepartment] = useState("");
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [clientDoamin, setClientDomain] = useState<string>("");
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [localDateRange, setLocalDateRange] = useState(customDateRange);
  const [clientOrgId, setClientOrgId] = useState<string>("");
  const [subOrgId, setSubOrgId] = useState<string>("");
  const [clientName, setClientName] = useState<string>("");
  useEffect(() => {
    if (customDateRange) setLocalDateRange(customDateRange);
  }, [customDateRange]);
  
  const handleDateRangeChange = (range: { startDate: string; endDate: string }) => {
    setLocalDateRange(range);
    if (setCustomDateRange) {
      setCustomDateRange(range);
    }
  };

  const getClassificationType = (
    selectedNps?: string | null,
    viewMode?: "count" | "revenue"
  ) => {
    if (!selectedNps) return "";
  
    const map: Record<string, string> = {
      promoters:
        viewMode === "revenue"
          ? "PROMOTER_MRR_CLIENTS"
          : "PROMOTER_CLIENTS",
  
      neutral:
        viewMode === "revenue"
          ? "NEUTRAL_MRR_CLIENTS"
          : "NEUTRAL_CLIENTS",
  
      detractors:
        viewMode === "revenue"
          ? "DETRACTOR_MRR_CLIENTS"
          : "DETRACTOR_CLIENTS",
  
      notRated: "NOT_RATED_CLIENTS",
      noRevenue: "NO_MRR_CLIENTS",
    };
  
    return map[selectedNps] || "";
  };  
  const getBackendSortField = (uiField?: string) => {
    const map: Record<string, string> = {
      domain: "CLIENT_NAME",
      mrr: "MRR",
      num_meetings: "TOTAL_INTERACTIONS",
      lastInteraction: "LAST_INTERACTION_DATE",
    };
  
    return map[uiField || ""] || "CLIENT_NAME";
  };  
  const classificationType = getClassificationType(selectedNps, viewMode);

  const extractClientsFromBusinessResponse = (
    response: any,
    selectedNps?: string | null,
    viewMode?: "count" | "revenue"
  ) => {
    if (!response) return { clients: [], totalRecords: 0 };
  
    if (viewMode === "revenue") {
      const mrr = response.clientsByMrrClassification;
  
      if (!mrr) return { clients: [], totalRecords: 0 };
  
      const map: Record<string, any[]> = {
        promoters: mrr.promoterMrrClients,
        neutral: mrr.neutralMrrClients,
        detractors: mrr.detractorMrrClients,
        noRevenue: mrr.noMrrClients,
      };
  
      return {
        clients: map[selectedNps || ""] || [],
        totalRecords: mrr.totalRecords || 0,
      };
    }
  
    // COUNT MODE
    const cls = response.clientsByClassification;
  
    if (!cls) return { clients: [], totalRecords: 0 };
  
    const map: Record<string, any[]> = {
      promoters: cls.promoterClients,
      neutral: cls.neutralClients,
      detractors: cls.detractorClients,
      notRated: cls.notRatedClients,
    };
  
    return {
      clients: map[selectedNps || ""] || [],
      totalRecords: cls.totalRecords || 0,
    };
  };
  
  useEffect(() => {
    if (localDateRange?.startDate && localDateRange?.endDate) {
      try {
        const savedFilters = JSON.parse(localStorage.getItem("businessOverviewFilters") || "{}");
        localStorage.setItem(
          "businessOverviewFilters",
          JSON.stringify({ ...savedFilters, customDateRange: localDateRange })
        );
      } catch (err) {
        console.error("Failed to save date range:", err);
      }
    }
  }, [localDateRange]);
  const handleNewUserTeamData = (data: {
    currentUser: string;
    clickedUserInfo?: UserInfo;
    currentManager?: UserInfo;
    departments: any[];
    subordinates: UserInfo[];
    managerOptions: UserInfo[];
  }) => {
    setNewUserTeamData(data);
  };

  useEffect(()=> {
    if(clickeduserEmail) {
      setSelectedUser({email: clickeduserEmail})
    }
  },[clickeduserEmail]);

  const {
    isLoading: isBusinessClientsLoading,
    isFetching: isBusinessClientsFetching,
    isSuccess: isBusinessClientsSuccess,
    isError: isBusinessClientsError,
    error: businessError,
    data: isBusinessClientsData,
    refetch: refetchBussinessClients
  } = ClientService.useGetClientBusinessStats(
    {
      startTime: convertToUTC(localDateRange?.startDate || ""),
      endTime: convertToUTC(localDateRange?.endDate || ""),
      classificationType,
      sortBy: getBackendSortField(sort?.sort_field),
      sortOrder: sort?.sort_order?.toUpperCase() || "ASC",
      offset: currentPageIndex,
      limit: pageSize,
      employeePorId: loggedInProfileOrgId,
      timeZone: getNormalizedTimeZone(),
    },
    !!classificationType &&
      !!localDateRange?.startDate &&
      !!localDateRange?.endDate
  );

  useEffect(() => {
    if (isSlideOverOpen && localDateRange?.startDate && localDateRange?.endDate) {
      refetchBussinessClients();
    }
  }, [isSlideOverOpen, localDateRange?.startDate, localDateRange?.endDate]);
  
  useEffect(() => {
    setShowSpinner(isBusinessClientsLoading || isBusinessClientsFetching);

    if (isBusinessClientsSuccess && isBusinessClientsData?.data) {
      const { clients, totalRecords } =
        extractClientsFromBusinessResponse(
          isBusinessClientsData.data,
          selectedNps,
          viewMode
        );

      setAccountInsights(clients);
      setTotalRecord(totalRecords);
    }

    if (isBusinessClientsError) {
      showSnackbar(
        "error",
        businessError?.message || "Failed to load data"
      );
      setAccountInsights([]);
    }
  }, [
    isBusinessClientsLoading,
    isBusinessClientsFetching,
    isBusinessClientsSuccess,
    isBusinessClientsError,
    isBusinessClientsData,
    selectedNps,
    viewMode,
  ]);

  useEffect(() => {
    const filteredRows = generateRows(
      accountInsights,
      true,
      selectedNps || null,
      loggedInEmail,
      localDateRange,
      isClientInteraction,
      onClientClick,
      highlightedClient,
      isSlideOverOpen,
      parentPageIdentifier,
      slideOverParent,
      localDateRange,
      clickeduserEmail
    );
  
    setRows(filteredRows);
  }, [
    accountInsights,
    selectedNps,
    loggedInEmail,
    localDateRange,
  ]);  


  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPageIndex(1);
    setPageSize(newPageSize);
  };
  const onSortingChange = (sorting: any[]) => {
    if (sorting && sorting.length > 0) {
      setSort &&
        setSort({
          sort_field: sorting[0].id,
          sort_order: sorting[0].desc === true ? "desc" : "asc",
        });
    }
  };

  const onClientClick = (ClientDomain: string, clientName: string,slideOver: boolean, clientOrgId: string, subOrgId: string) => {
    setClientDomain(ClientDomain);
    setClientName(clientName);
    setSlideOverOpen(slideOver);
    setClientOrgId(clientOrgId);
    setSubOrgId(subOrgId);
  };
  useEffect(() => {
    const selected = sessionStorage.getItem("selectedClient");
    if (selected) {
      setHighlightedClient(selected);
      const timer = setTimeout(() => {
        setHighlightedClient(null);
        sessionStorage.removeItem("selectedClient");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  const columns = getColumns();
  return (
    <div className="client-rating-overview">
      <div className="client-rating-overview__header">
        <div className="client-rating-overview__left">
          <h1 className="client-rating-overview__nps">
            {selectedNps === "noRevenue"
              ? "Clients Without Assigned MRR"
              : selectedNps
              ? capitalizeFirstLetter(selectedNps)
              : ""}
          </h1>
          <div style={{ visibility: slideOverParent === "analytics-slideover" ? "hidden" : "visible" }}>
          {parentPageIdentifier !== "user-analytics" && (
          <UserTeam
            setDepartment={setDepartment}
            setSelectedUser={setSelectedUser}
            loggedInEmail={loggedInEmail}
            showDropdown={true}
            display={false}
            onDataUpdate={handleNewUserTeamData}
            showDomainFirst={true}
            parent_page="client_stats"
            />
          )}
          </div>
        </div>
        <div className="client-rating-overview__right">
          <CustomDatePicker
            customDateRange={localDateRange}
            isSlideOverOpen={isSlideOverOpen}
            setCustomDateRange={(range) => handleDateRangeChange(range)}
            disabled={slideOverParent === "analytics-slideover"}
          />
        </div>
      </div>

      <Box sx={{ width: "100%", height: "100%" }}>
        <BasicDatatable
          columns={columns}
          rows={rows}
          rowCount={totalRecord}
          initialSort={initialSort}
          onPageSizeChange={(newPageSize: number | null | undefined) => {
            if (newPageSize !== null && newPageSize !== undefined) {
              handlePageSizeChange(newPageSize);
            }
          }}
          onSortingChange={onSortingChange}
          onPaginationChange={handlePageIndexChange}
          minHeight="400px"
          columnVisibility={{
            managerTime: false,
            analystTime: false,
            managerTalkTime:false,
            analystTalkTime: false,
          }}
          showSpinner={showSpinner}
        />
      </Box>
      <SlideOver isOpen={slideOverOpen} onClose={() => setSlideOverOpen(false)}>
          <ClientInteractionActionView
            selectedNps={selectedNps}
            clientDomain={clientDoamin}
            isSlideOverOpen={slideOverOpen}
            slideOverParent={slideOverParent}
            selectedUser={selectedUser}
            parentPageIdentifier={parentPageIdentifier}
            relationshipType="client"
            customDateRange={localDateRange}
            selectedTab={selectedTab}
            clickeduserEmail={clickeduserEmail}
            userDetails={ {
              userEmail: userEmail || "",
              userProfileOrgId: ""
            } }
            clientOrgId={clientOrgId}
            subOrgId={subOrgId}
            clientName={clientName}
          />
      </SlideOver>
    </div>
  );
};

export default ClientRatingOverview;
